import { useMemo } from "react";
import { Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceArea, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip } from "@/components/dashboard/ChartTooltip";
import { CHART, fmtInt, fmtPct, fmtMYR, fmtDate, DAY_FULL, tickStyle } from "@/lib/format";
import { useScope } from "@/lib/scope";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ legend */
const LEGEND_ITEMS = [
  { key: "MY", label: "MY public holiday", color: CHART.holiday.MY, marker: "line" },
  { key: "SG", label: "SG public holiday", color: CHART.holiday.SG, marker: "line" },
  { key: "JOHOR", label: "Johor holiday", color: CHART.holiday.JOHOR, marker: "line" },
  { key: "school", label: "School holidays", color: CHART.holiday.school, marker: "band" },
  { key: "payday", label: "Payday", color: CHART.holiday.payday, marker: "dashed" },
  { key: "custom", label: "Your events", color: CHART.holiday.custom, marker: "dot" },
  { key: "demand", label: "Chinese travel window", color: CHART.holiday.demand, marker: "band" },
];

export const HolidayLegend = ({ className, compact = false }) => (
  <div className={cn("flex flex-wrap gap-x-3 gap-y-1.5", className)} data-testid="holiday-legend">
    {LEGEND_ITEMS.map((l) => (
      <span key={l.key} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {l.marker === "band" ? (
          <span className="h-3 w-4 rounded-sm" style={{ background: l.color, opacity: 0.35 }} />
        ) : l.marker === "dot" ? (
          <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
        ) : (
          <span className="h-3 w-0 border-l-2" style={{ borderColor: l.color, borderStyle: l.marker === "dashed" ? "dashed" : "solid" }} />
        )}
        {!compact && l.label}
      </span>
    ))}
  </div>
);

/* ------------------------------------------------------------------ overlay builder */
const spans = (events, type, start, end) => {
  // merge overlapping ranges of the same type into bands clipped to the window
  const rs = events
    .filter((e) => e.type === type)
    .map((e) => [e.start < start ? start : e.start, e.end > end ? end : e.end, e])
    .sort((a, b) => (a[0] < b[0] ? -1 : 1));
  const out = [];
  rs.forEach(([s, e, ev]) => {
    const last = out[out.length - 1];
    if (last && s <= last[1]) {
      last[1] = e > last[1] ? e : last[1];
      last[2].push(ev);
    } else out.push([s, e, [ev]]);
  });
  return out;
};

/**
 * Builds Recharts overlay elements for the period calendar.
 * rows must carry `date`; the x-axis dataKey must be `date`.
 */
export const calendarOverlays = (events = [], rows = [], { showBands = true, showLines = true, showDots = true, dotY } = {}) => {
  if (!rows.length || !events.length) return [];
  const start = rows[0].date;
  const end = rows[rows.length - 1].date;
  const dates = new Set(rows.map((r) => r.date));
  const out = [];
  if (showBands) {
    spans(events, "school_holiday", start, end).forEach(([s, e], i) => out.push(<ReferenceArea key={`sch-${i}`} x1={s} x2={e} fill={CHART.holiday.school} fillOpacity={0.09} strokeOpacity={0} ifOverflow="extendDomain" />));
    spans(events, "demand", start, end).forEach(([s, e], i) => out.push(<ReferenceArea key={`dem-${i}`} x1={s} x2={e} fill={CHART.holiday.demand} fillOpacity={0.08} strokeOpacity={0} />));
  }
  if (showLines) {
    const seen = new Set();
    events
      .filter((e) => e.type === "public_holiday")
      .forEach((e) => {
        let d = e.start;
        while (d <= e.end) {
          if (dates.has(d) && !seen.has(`${d}-${e.market}`)) {
            seen.add(`${d}-${e.market}`);
            out.push(<ReferenceLine key={`ph-${d}-${e.market}`} x={d} stroke={CHART.holiday[e.market] || CHART.holiday.MY} strokeWidth={1.25} strokeOpacity={0.9} />);
          }
          d = nextDay(d);
        }
      });
    events.filter((e) => e.type === "payday" && dates.has(e.start)).forEach((e) => out.push(<ReferenceLine key={`pd-${e.start}`} x={e.start} stroke={CHART.holiday.payday} strokeDasharray="4 4" strokeWidth={1.25} />));
  }
  if (showDots && dotY !== undefined) {
    events.filter((e) => e.type === "custom").forEach((e) => {
      let d = e.start;
      while (d <= e.end) {
        if (dates.has(d)) out.push(<ReferenceDot key={`cu-${d}-${e.id}`} x={d} y={dotY} r={4} fill={CHART.holiday.custom} stroke="none" />);
        d = nextDay(d);
      }
    });
  }
  return out;
};

const nextDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const xTick = (rows) => (rows.length > 45 ? 6 : rows.length > 20 ? 2 : 0);
const xLabel = (iso, rows) => (rows.length <= 7 ? `${DAY_FULL[rows.find((r) => r.date === iso)?.day]?.slice(0, 3) || ""} ${fmtDate(iso)}` : fmtDate(iso));

const eventLine = (row) => {
  const parts = [];
  if (row?.events?.length) parts.push(row.events.slice(0, 2).join(" · "));
  if (row?.partial) parts.push("part-day export");
  if (row?.in_progress) parts.push("still counting");
  return parts.join(" · ");
};

/* ------------------------------------------------------------------ footfall trend (current vs previous, with overlays) */
export const TrendChart = ({ rows = [], prevRows = [], events = [], height = 320, metric = "entered", showPrev = true, testId }) => {
  const { overlay } = useScope();
  const data = useMemo(
    () =>
      rows.map((r, i) => ({
        ...r,
        value: r.has_data ? r[metric] : null,
        prev: showPrev && prevRows[i]?.has_data ? prevRows[i][metric] : null,
        prevDate: prevRows[i]?.date,
      })),
    [rows, prevRows, metric, showPrev]
  );
  const maxY = Math.max(0, ...data.map((d) => Math.max(d.value || 0, d.prev || 0)));
  const isPct = metric === "walk_in_rate";
  return (
    <div className="chart-frame" data-testid={testId || "trend-chart"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          {overlay && calendarOverlays(events, rows, { dotY: maxY * 1.02 })}
          <XAxis dataKey="date" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={xTick(rows)} tickFormatter={(v) => xLabel(v, rows)} minTickGap={16} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} tickFormatter={(v) => (isPct ? `${v}%` : v >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={48} />
          <Tooltip
            content={
              <ChartTooltip
                labelFormatter={(l, p) => {
                  const row = p?.[0]?.payload;
                  return `${DAY_FULL[row?.day] || ""} ${fmtDate(l)}${eventLine(row) ? ` · ${eventLine(row)}` : ""}`;
                }}
                formatter={(v, name, p) => [isPct ? fmtPct(v) : fmtInt(v), name === "prev" ? `Previous (${fmtDate(p.payload.prevDate)})` : isPct ? "Walk-in rate" : "Visitors"]}
              />
            }
          />
          {showPrev && <Line type="monotone" dataKey="prev" stroke={CHART.prev} strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls isAnimationActive={false} />}
          <Area type="monotone" dataKey="value" stroke={CHART.gold} strokeWidth={2} fill={CHART.gold} fillOpacity={0.12} dot={rows.length <= 14 ? { r: 3, fill: CHART.gold, strokeWidth: 0 } : false} activeDot={{ r: 4, strokeWidth: 2, stroke: CHART.gold, fill: "hsl(var(--background))" }} connectNulls={false} isAnimationActive={false} />
          {showPrev && <Legend verticalAlign="top" height={24} iconType="plainline" formatter={(v) => (v === "prev" ? "Previous period" : isPct ? "Walk-in rate" : "Visitors")} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ weekday profile */
export const WeekdayChart = ({ rows = [], height = 240, testId }) => {
  const data = rows.map((r) => ({ ...r, cur: r.avg, prev: r.prev_avg }));
  return (
    <div className="chart-frame" data-testid={testId || "weekday-chart"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 8, left: -12, bottom: 0 }} barCategoryGap={18}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis dataKey="day" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
          <Tooltip content={<ChartTooltip labelFormatter={(l, p) => `${DAY_FULL[l]} · index ${p?.[0]?.payload?.index ?? "—"}`} formatter={(v, name) => [fmtInt(v), name === "prev" ? "Previous avg / day" : "Avg visitors / day"]} />} cursor={{ fill: "hsl(var(--gold) / 0.06)" }} />
          <Bar dataKey="cur" radius={[6, 6, 0, 0]} fill={CHART.gold} isAnimationActive={false} />
          <Line type="monotone" dataKey="prev" stroke={CHART.prev} strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2.5 }} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------------------------------------------ basket + conversion by weekday (sales) */
export const AtvWeekdayChart = ({ rows = [], height = 240, testId }) => (
  <div className="chart-frame" data-testid={testId || "atv-weekday-chart"} style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={rows} margin={{ top: 10, right: 0, left: -8, bottom: 0 }} barCategoryGap={18}>
        <CartesianGrid stroke={CHART.grid} vertical={false} />
        <XAxis dataKey="day" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} />
        <YAxis yAxisId="atv" tick={tickStyle} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `RM${v}`} />
        <YAxis yAxisId="conv" orientation="right" tick={tickStyle} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<ChartTooltip labelFormatter={(l, p) => `${DAY_FULL[l] || l} · ${p?.[0]?.payload?.days ?? 0} day(s)`} formatter={(v, name) => [name === "atv" ? fmtMYR(v, 2) : fmtPct(v), name === "atv" ? "Avg basket" : "Conversion"]} />} cursor={{ fill: "hsl(var(--gold) / 0.06)" }} />
        <Bar yAxisId="atv" dataKey="atv" fill={CHART.copper} radius={[6, 6, 0, 0]} isAnimationActive={false} />
        <Line yAxisId="conv" type="monotone" dataKey="conversion" stroke={CHART.sky} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
        <Legend verticalAlign="top" height={24} formatter={(v) => (v === "atv" ? "Avg basket (RM)" : "Conversion (%)")} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

/* ------------------------------------------------------------------ who visits: gender split + age bands */
export const GenderSplit = ({ gender, testId }) => {
  if (!gender || gender.male_share === null || gender.male_share === undefined)
    return <div className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/70 p-3" data-testid="gender-split-empty">Gender was not captured in this window.</div>;
  const d = gender.prev_female_share !== null && gender.prev_female_share !== undefined ? +(gender.female_share - gender.prev_female_share).toFixed(1) : null;
  return (
    <div data-testid={testId || "gender-split"} className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: CHART.sky }} /> Male <span className="font-mono tabular text-foreground">{fmtPct(gender.male_share, 0)}</span></span>
        <span className="inline-flex items-center gap-1.5">Female <span className="font-mono tabular text-foreground">{fmtPct(gender.female_share, 0)}</span><span className="h-2 w-2 rounded-sm" style={{ background: CHART.rose }} /></span>
      </div>
      <div className="h-2.5 w-full rounded-full overflow-hidden flex bg-muted/40" role="img" aria-label={`Male ${gender.male_share}% / Female ${gender.female_share}%`}>
        <span style={{ width: `${gender.male_share}%`, background: CHART.sky }} />
        <span style={{ width: `${gender.female_share}%`, background: CHART.rose }} />
      </div>
      <div className="text-[11px] text-muted-foreground font-mono">{fmtInt(gender.male)} men · {fmtInt(gender.female)} women{d !== null ? ` · female share ${d > 0 ? "+" : ""}${d} pts vs previous` : ""}</div>
    </div>
  );
};

export const AgeBands = ({ bands = [], testId }) => {
  if (!bands.length) return <div className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/70 p-3" data-testid="age-bands-empty">Age bands were not captured in this window.</div>;
  const max = Math.max(...bands.map((b) => b.share || 0), 1);
  return (
    <ul className="space-y-1.5" data-testid={testId || "age-bands"}>
      {bands.map((b) => {
        const d = b.prev_share !== null && b.prev_share !== undefined && b.share !== null ? +(b.share - b.prev_share).toFixed(1) : null;
        return (
          <li key={b.band} className="grid grid-cols-[96px_1fr_auto] items-center gap-2 text-xs" data-testid={`age-band-${b.band.replace(/\s+/g, "-").toLowerCase()}`}>
            <span className="truncate text-muted-foreground">{b.band}</span>
            <span className="h-2 rounded-full bg-muted/40 overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${((b.share || 0) / max) * 100}%`, background: CHART.gold }} /></span>
            <span className="font-mono tabular text-right w-[108px]">{fmtPct(b.share, 1)}{d !== null && <span className={cn("ml-1.5 text-[10px]", d > 0 ? "text-success" : d < 0 ? "text-danger" : "text-muted-foreground")}>{d > 0 ? "+" : ""}{d}</span>}</span>
          </li>
        );
      })}
    </ul>
  );
};

export const SalesTrendChart = ({ rows = [], events = [], height = 320, testId }) => {
  const { overlay } = useScope();
  const maxRev = Math.max(0, ...rows.map((r) => r.revenue || 0));
  return (
    <div className="chart-frame" data-testid={testId || "sales-trend-chart"} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 10, right: 0, left: -12, bottom: 0 }}>
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          {overlay && calendarOverlays(events, rows, { dotY: maxRev * 1.02 })}
          <XAxis dataKey="date" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={xTick(rows)} tickFormatter={(v) => xLabel(v, rows)} minTickGap={16} />
          <YAxis yAxisId="rev" tick={tickStyle} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
          <YAxis yAxisId="vis" orientation="right" tick={tickStyle} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
          <Tooltip content={<ChartTooltip labelFormatter={(l, p) => `${DAY_FULL[p?.[0]?.payload?.day] || ""} ${fmtDate(l)}${p?.[0]?.payload?.events?.length ? ` · ${p[0].payload.events.slice(0, 2).join(" · ")}` : ""}`} formatter={(v, name) => [name === "revenue" ? fmtMYR(v) : name === "conversion" ? fmtPct(v) : fmtInt(v), name === "revenue" ? "Revenue" : name === "visitors" ? "Visitors" : "Conversion"]} />} cursor={{ fill: "hsl(var(--gold) / 0.06)" }} />
          <Bar yAxisId="rev" dataKey="revenue" fill={CHART.gold} fillOpacity={0.85} radius={[5, 5, 0, 0]} isAnimationActive={false} />
          <Line yAxisId="vis" type="monotone" dataKey="visitors" stroke={CHART.sky} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Legend verticalAlign="top" height={24} formatter={(v) => (v === "revenue" ? "Revenue (RM)" : "Visitors")} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
